/**
 * Hierarchy / org-chart layout — builds a tree from node levels, places leaves evenly
 * across the width and parents centered above their children, with connector lines.
 */
import { buildTree, paletteColor } from '../smartArtUtils';

const PAD = 6;
const SIDE_PAD = 3; // outer left/right padding (%) — small so wide containers don't show large side margins
const BOX_H = 14;
const BOX_W = 30;

export const layoutHierarchy = (nodes, theme) => {
  const boxes = [];
  const connectors = [];
  if (!nodes.length) return { boxes, connectors };

  const roots = buildTree(nodes);

  // max depth
  let maxDepth = 0;
  const measure = (node, depth) => {
    maxDepth = Math.max(maxDepth, depth);
    (node.children || []).forEach((c) => measure(c, depth + 1));
  };
  roots.forEach((r) => measure(r, 0));

  // count leaves to spread horizontally
  let leafSlot = 0;
  const totalLeaves = (() => {
    let count = 0;
    const countLeaves = (node) => {
      if (!node.children || node.children.length === 0) count += 1;
      else node.children.forEach(countLeaves);
    };
    roots.forEach(countLeaves);
    return Math.max(count, 1);
  })();

  const innerW = 100 - SIDE_PAD * 2;
  const slotW = innerW / totalLeaves;
  const rowGap = (100 - PAD * 2 - BOX_H) / Math.max(maxDepth, 1);
  // shrink boxes so siblings never touch — keep ~18% of each slot as the gap
  const boxW = Math.max(8, Math.min(BOX_W, slotW * 0.82));
  // spread leaves edge-to-edge: outermost leaves sit against the side padding,
  // the rest evenly between, so the tree fills the width instead of clustering centrally
  const leafSpan = 100 - SIDE_PAD * 2 - boxW;
  const leafStep = totalLeaves > 1 ? leafSpan / (totalLeaves - 1) : 0;
  const centers = {};

  // assign x (center) per node
  const assign = (node, depth) => {
    const yCenter = PAD + depth * rowGap + BOX_H / 2;
    let xCenter;
    if (!node.children || node.children.length === 0) {
      xCenter = totalLeaves > 1 ? SIDE_PAD + boxW / 2 + leafSlot * leafStep : 50;
      leafSlot += 1;
    } else {
      const childCenters = node.children.map((c) => assign(c, depth + 1));
      xCenter = childCenters.reduce((s, c) => s + c.x, 0) / childCenters.length;
    }
    centers[node.id] = { x: xCenter, y: yCenter };
    return { x: xCenter, y: yCenter };
  };
  roots.forEach((r) => assign(r, 0));

  nodes.forEach((node, i) => {
    const c = centers[node.id];
    if (!c) return;
    boxes.push({
      nodeId: node.id,
      xPct: c.x - boxW / 2,
      yPct: c.y - BOX_H / 2,
      wPct: boxW,
      hPct: BOX_H,
      shapeType: 'rectangle',
      fill: paletteColor(theme, node.level),
      textColor: theme.textColor,
    });
  });

  // connectors: parent bottom-center -> child top-center
  const link = (node) => {
    const pc = centers[node.id];
    (node.children || []).forEach((child) => {
      const cc = centers[child.id];
      connectors.push({
        x1Pct: pc.x, y1Pct: pc.y + BOX_H / 2,
        x2Pct: cc.x, y2Pct: cc.y - BOX_H / 2,
        kind: 'line',
        // Animation reveals a connector with the node it leads into.
        toNodeId: child.id,
      });
      link(child);
    });
  };
  roots.forEach(link);

  return { boxes, connectors };
};
